/** @jsxImportSource theme-ui */
import React, { useMemo, useState } from 'react';
import { Badge, Box, Flex, Heading, Label, Spinner, Text } from 'theme-ui';
import { Formik } from 'formik';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDialogState } from 'reakit/Dialog';
import {
    useGetCompanyQuery,
    useGetWidgetConfigurationsQuery,
    useGetWidgetConfigurationSchemasQuery,
    usePostWidgetConfigurationMutation,
    usePutWidgetConfigurationMutation,
    useDeleteWidgetConfigurationMutation,
    useResetWidgetConfigurationMutation,
    useBumpWidgetConfigurationVersionMutation,
} from '@/store/apiService';
import { Container } from '@/components/styled';
import Button from '@/components/Button';
import { GrayFormikField } from '@/components/GrayFormikField';
import SearchBy from '@/components/SearchBy';
import { ConfirmationModal } from '@/components/ConfirmationModal';
import {
    UpdateWidgetConfigurationBody,
    WidgetConfigurationResponse,
    WidgetConfigurationSchemaResponse,
} from '@/types/types';
import WidgetPreviewDialog from './WidgetPreviewDialog';
import ConfigurationJsonField, { validateConfigurationJson } from './ConfigurationJsonField';
import { buildBasicEmbedScript, buildFullEmbedScript } from './embedScript';
import { resolveEmbedScriptUrl } from './embedScriptUrl';
import FitViewportHelp, { FIT_VIEWPORT_HELP_TEXT } from './FitViewportHelp';

const COPIED_FEEDBACK_MS = 2000;

// legacy-api's GenerateFullSlug always appends "-<last 3 hex chars of CompanyId>" to
// whatever Slug is sent, and WidgetConfigurationResponse.Slug is already-suffixed.
// Editing must strip that suffix before showing/resubmitting, or the suffix doubles
// up (and stays subtly wrong forever, or is misread as an attempted slug change).
const stripCompanySuffix = (slug: string, companyId: string): string => {
    const suffix = companyId.replace(/-/g, '').slice(-3);
    return slug.endsWith(`-${suffix}`) ? slug.slice(0, -(suffix.length + 1)) : slug;
};

type View = 'list' | 'create';

type ConfirmAction = 'delete' | 'reset' | 'bumpFactory' | 'bumpMigration' | null;

const ConfigurationRow: React.FC<{
    config: WidgetConfigurationResponse;
    expanded: boolean;
    onToggle: () => void;
    companySitepath: string;
    embedScriptUrl: string;
    schemas: WidgetConfigurationSchemaResponse[];
}> = ({ config, expanded, onToggle, companySitepath, embedScriptUrl, schemas }) => {
    const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);
    const [putConfiguration] = usePutWidgetConfigurationMutation();
    const [deleteConfiguration, deleteState] = useDeleteWidgetConfigurationMutation();
    const [resetConfiguration, resetState] = useResetWidgetConfigurationMutation();
    const [bumpConfigurationVersion, bumpState] = useBumpWidgetConfigurationVersionMutation();
    const [copied, setCopied] = useState(false);
    const [copiedScript, setCopiedScript] = useState(false);
    const [copiedFullScript, setCopiedFullScript] = useState(false);
    const [fitViewportEnabled, setFitViewportEnabled] = useState(true);
    const previewDialog = useDialogState();

    const url = useMemo(
        () =>
            embedScriptUrl
                ? `${new URL(embedScriptUrl).origin}/book/services?wid=${config.Id}`.toLowerCase()
                : '',
        [embedScriptUrl, config.Id]
    );

    const previewConfig = useMemo<Record<string, unknown> | null>(() => {
        if (!config.Configuration) return null;
        try {
            return JSON.parse(config.Configuration) as Record<string, unknown>;
        } catch {
            return null;
        }
    }, [config.Configuration]);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
        } catch {
            // clipboard unavailable; ignore
        }
    };

    const handleCopyScript = async () => {
        const embedScript = buildBasicEmbedScript({
            embedScriptUrl,
            companySitepath,
            configId: config.Id!,
            fitViewport: fitViewportEnabled,
        });
        await navigator.clipboard.writeText(embedScript);
        setCopiedScript(true);
        setTimeout(() => setCopiedScript(false), COPIED_FEEDBACK_MS);
    };

    const handleCopyFullScript = async () => {
        const embedScript = buildFullEmbedScript({
            embedScriptUrl,
            companySitepath,
            configId: config.Id!,
            configuration: JSON.parse(config.Configuration!),
            fitViewport: fitViewportEnabled,
        });
        await navigator.clipboard.writeText(embedScript);
        setCopiedFullScript(true);
        setTimeout(() => setCopiedFullScript(false), COPIED_FEEDBACK_MS);
    };

    const hasConfiguration = Boolean(config.Configuration);

    return (
        <Box sx={{ mb: 3, borderRadius: 'sm', backgroundColor: 'gray.1' }}>
            <Flex
                sx={{
                    gap: 3,
                    p: 3,
                    cursor: 'pointer',
                    alignItems: 'center',
                    borderTopLeftRadius: 'sm',
                    borderTopRightRadius: 'sm',
                    '&:hover': { backgroundColor: 'gray.2' },
                }}
                onClick={onToggle}
            >
                <Badge sx={{ backgroundColor: 'gray.4', color: 'gray.7', fontSize: 0 }}>
                    #{config.Id}
                </Badge>
                <Text sx={{ fontWeight: 'bold' }}>{config.Name}</Text>
                <Text sx={{ color: 'gray.6', fontSize: 0 }}>{config.Slug}</Text>
                <Text sx={{ color: 'gray.6', fontSize: 0 }}>
                    Schema v{config.SchemaVersion}
                </Text>
                {config.IsHomepageDefault && (
                    <Badge sx={{ backgroundColor: 'blue.4', fontSize: 0 }}>Homepage default</Badge>
                )}
                <Text sx={{ color: 'gray.6', fontSize: 0, ml: 'auto' }}>
                    {config.CreatedDate ? new Date(config.CreatedDate).toLocaleDateString('sv-SE') : ''}
                </Text>
                <Text sx={{ fontSize: 1, color: 'gray.5' }}>{expanded ? '▲' : '▼'}</Text>
            </Flex>

            <Flex sx={{ gap: 2, px: 3, pb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
                <Label sx={{ width: 'auto', alignItems: 'center', gap: 1, mr: 3 }}>
                    <input
                        type="checkbox"
                        checked={fitViewportEnabled}
                        onChange={(e) => setFitViewportEnabled(e.target.checked)}
                    />
                    Fit viewport
                    <FitViewportHelp text={FIT_VIEWPORT_HELP_TEXT} />
                </Label>
                <Button variant="secondary" type="button" onClick={handleCopy} disabled={!url}>
                    {copied ? 'Copied!' : 'Copy link'}
                </Button>
                <Button
                    variant="secondary"
                    type="button"
                    onClick={handleCopyScript}
                    disabled={!url || !embedScriptUrl}
                >
                    {copiedScript ? 'Copied!' : 'Copy embed script'}
                </Button>
                <Button
                    variant="secondary"
                    type="button"
                    onClick={handleCopyFullScript}
                    disabled={!url || !embedScriptUrl || !hasConfiguration}
                >
                    {copiedFullScript ? 'Copied!' : 'Copy full script'}
                </Button>
                <Button
                    variant="secondary"
                    type="button"
                    onClick={() => previewDialog.show()}
                    disabled={!embedScriptUrl || !hasConfiguration}
                >
                    Preview
                </Button>
            </Flex>

            {expanded && (
                <Box sx={{ px: 3, pb: 3 }}>
                    {confirmAction === 'delete' && (
                        <ConfirmationModal
                            hideOnClickOutside
                            loading={deleteState.isLoading}
                            headingText={`Delete "${config.Name}"?`}
                            content="This will permanently delete this widget configuration."
                            onNo={() => setConfirmAction(null)}
                            onYes={() => {
                                deleteConfiguration({ Id: config.Id!, CompanyId: config.CompanyId })
                                    .unwrap()
                                    .then(() => setConfirmAction(null))
                                    .catch((err) => {
                                        setConfirmAction(null);
                                        toast.error(
                                            err?.data?.ResponseStatus?.Message ??
                                            'Failed to delete widget configuration',
                                            { autoClose: 5000 },
                                        );
                                    });
                            }}
                        />
                    )}
                    {confirmAction === 'reset' && (
                        <ConfirmationModal
                            hideOnClickOutside
                            loading={resetState.isLoading}
                            headingText={`Reset "${config.Name}"?`}
                            content="Reset this configuration to its factory default? This only works on the homepage-default configuration."
                            onNo={() => setConfirmAction(null)}
                            onYes={() => {
                                resetConfiguration({ Id: config.Id!, CompanyId: config.CompanyId })
                                    .unwrap()
                                    .then(() => setConfirmAction(null))
                                    .catch((err) => {
                                        setConfirmAction(null);
                                        toast.error(
                                            err?.data?.ResponseStatus?.Message ??
                                            'Failed to reset widget configuration',
                                            { autoClose: 5000 },
                                        );
                                    });
                            }}
                        />
                    )}
                    {confirmAction === 'bumpFactory' && (
                        <ConfirmationModal
                            hideOnClickOutside
                            loading={bumpState.isLoading}
                            headingText="Bump to next schema version (factory defaults)?"
                            content="Bump this configuration to the next schema version, overwriting its Configuration with that schema's factory default. This only works on the homepage-default configuration and requires the next schema version to have a default configuration authored."
                            onNo={() => setConfirmAction(null)}
                            onYes={() => {
                                bumpConfigurationVersion({
                                    Id: config.Id!,
                                    CompanyId: config.CompanyId,
                                    UseFactoryDefaults: true,
                                })
                                    .unwrap()
                                    .then(() => setConfirmAction(null))
                                    .catch((err) => {
                                        setConfirmAction(null);
                                        toast.error(
                                            err?.data?.ResponseStatus?.Message ??
                                            'Failed to bump widget configuration version',
                                            { autoClose: 5000 },
                                        );
                                    });
                            }}
                        />
                    )}
                    {confirmAction === 'bumpMigration' && (
                        <ConfirmationModal
                            hideOnClickOutside
                            loading={bumpState.isLoading}
                            headingText="Bump to next schema version (apply migration)?"
                            content="Bump this configuration to the next schema version by applying that schema's migration patch to the current Configuration. This only works on the homepage-default configuration and requires a migration patch to be authored for the next schema version."
                            onNo={() => setConfirmAction(null)}
                            onYes={() => {
                                bumpConfigurationVersion({
                                    Id: config.Id!,
                                    CompanyId: config.CompanyId,
                                    UseFactoryDefaults: false,
                                })
                                    .unwrap()
                                    .then(() => setConfirmAction(null))
                                    .catch((err) => {
                                        setConfirmAction(null);
                                        toast.error(
                                            err?.data?.ResponseStatus?.Message ??
                                            'Failed to bump widget configuration version',
                                            { autoClose: 5000 },
                                        );
                                    });
                            }}
                        />
                    )}
                    <Formik
                        key={config.Id}
                        enableReinitialize
                        initialValues={{
                            name: config.Name ?? '',
                            slug: config.Slug ? stripCompanySuffix(config.Slug, config.CompanyId) : '',
                            description: config.Description ?? '',
                            configuration: config.Configuration ?? '',
                            schemaVersion: config.SchemaVersion ?? (0 as number),
                        }}
                        onSubmit={(values, { setErrors, setSubmitting }) => {
                            const jsonError = validateConfigurationJson(values.configuration);
                            if (jsonError) {
                                setErrors({ configuration: jsonError });
                                setSubmitting(false);
                                return;
                            }
                            // SchemaVersion must always be sent (API requires > 0 unconditionally).
                            // Slug is omitted for homepage-default configs: the API treats a missing
                            // Slug as "no change" there, but locks it against any other value.
                            const payload: UpdateWidgetConfigurationBody = {
                                Id: config.Id!,
                                CompanyId: config.CompanyId,
                                Name: values.name,
                                Description: values.description || undefined,
                                Configuration: values.configuration,
                                SchemaVersion: Number(values.schemaVersion),
                                Slug: config.IsHomepageDefault ? undefined : values.slug || undefined,
                            };
                            putConfiguration(payload)
                                .unwrap()
                                .then(() => onToggle())
                                .catch((err) => {
                                    setSubmitting(false);
                                    toast.error(
                                        err?.data?.ResponseStatus?.Message ??
                                        'Failed to update widget configuration',
                                        { autoClose: 5000 },
                                    );
                                });
                        }}
                    >
                        {(formProps) => {
                            const currentSchema = schemas.find(
                                (s) => s.Version === Number(formProps.values.schemaVersion),
                            )?.Schema;

                            return (
                                <Flex sx={{ gap: 3, alignItems: 'flex-start' }}>
                                    <Flex sx={{ flex: 1, minWidth: 0, flexDirection: 'column', gap: 3, pt: 2 }}>
                                        <GrayFormikField name="name" label="Name" required autoComplete="off" />
                                        <GrayFormikField
                                            name="slug"
                                            label="Slug"
                                            autoComplete="off"
                                            disabled={config.IsHomepageDefault}
                                        />
                                        <GrayFormikField type="textarea" name="description" label="Description" />
                                        <ConfigurationJsonField
                                            name="configuration"
                                            rows={48}
                                            schema={currentSchema}
                                            companySitepath={companySitepath}
                                            embedScriptUrl={embedScriptUrl}
                                            configId={config.Id}
                                            fitViewportEnabled={fitViewportEnabled}
                                            onFitViewportEnabledChange={setFitViewportEnabled}
                                        />
                                        <GrayFormikField
                                            type="number"
                                            name="schemaVersion"
                                            label="Schema version"
                                            required
                                            disabled={config.IsHomepageDefault}
                                        />
                                        {config.IsHomepageDefault && (
                                            <Text sx={{ fontSize: 0, color: 'gray.6' }}>
                                                Slug and schema version locked for homepage default
                                                configuration. Use &quot;Bump&quot; buttons to change schema
                                                version.
                                            </Text>
                                        )}
                                        <Flex sx={{ justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                                            <Flex sx={{ gap: 2, flexWrap: 'wrap' }}>
                                                <Button
                                                    variant="danger"
                                                    type="button"
                                                    onClick={() => setConfirmAction('delete')}
                                                >
                                                    Delete
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    type="button"
                                                    onClick={() => setConfirmAction('reset')}
                                                >
                                                    Reset
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    type="button"
                                                    onClick={() => setConfirmAction('bumpFactory')}
                                                >
                                                    Bump (factory defaults)
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    type="button"
                                                    onClick={() => setConfirmAction('bumpMigration')}
                                                >
                                                    Bump (apply migration)
                                                </Button>
                                            </Flex>
                                            <Flex sx={{ gap: 2 }}>
                                                <Button variant="secondary" type="button" onClick={onToggle}>
                                                    Cancel
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    isLoading={formProps.isSubmitting}
                                                    disabled={formProps.isSubmitting}
                                                    onClick={formProps.submitForm}
                                                >
                                                    Save
                                                </Button>
                                            </Flex>
                                        </Flex>
                                    </Flex>
                                </Flex>
                            );
                        }}
                    </Formik>
                </Box>
            )}

            <WidgetPreviewDialog
                dialog={previewDialog}
                configuration={previewConfig}
                companySitepath={companySitepath}
                embedScriptUrl={embedScriptUrl}
                url={url}
                hasConfiguration={hasConfiguration}
                onCopyLink={handleCopy}
                copiedLink={copied}
                onCopyScript={handleCopyScript}
                copiedScript={copiedScript}
                onCopyFullScript={handleCopyFullScript}
                copiedFullScript={copiedFullScript}
                fitViewport={fitViewportEnabled}
                onFitViewportChange={setFitViewportEnabled}
            />
        </Box>
    );
};

const WidgetConfigurations: React.FC = () => {
    const { companyId: routeCompanyId } = useParams<{ companyId?: string }>();
    const navigate = useNavigate();
    const [view, setView] = useState<View>('list');
    const [companyId, setCompanyId] = useState(routeCompanyId ?? '');
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
    const [postConfiguration] = usePostWidgetConfigurationMutation();

    const trimmed = companyId.trim();
    const backToCompanyId = trimmed || routeCompanyId;
    const { data, isLoading } = useGetWidgetConfigurationsQuery(
        { CompanyId: trimmed },
        { skip: trimmed.length === 0 },
    );
    const { data: companyData } = useGetCompanyQuery({ Id: trimmed }, { skip: trimmed.length === 0 });
    const { data: schemasData } = useGetWidgetConfigurationSchemasQuery({});
    const schemas = schemasData?.Results ?? [];
    const sitePath = (companyData?.SitePath ?? '').toLowerCase();
    const embedScriptUrl = resolveEmbedScriptUrl(companyData?.SitePath);

    const configurations = data?.Results ?? [];

    const toggleExpanded = (id: string) => {
        setExpandedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    if (view === 'create') {
        return (
            <Container>
                <Flex sx={{ alignItems: 'center', gap: 2, mb: 3 }}>
                    <Button variant="secondary" type="button" onClick={() => setView('list')}>
                        Back
                    </Button>
                    <Heading as="h2">Create widget configuration</Heading>
                </Flex>
                <Formik
                    initialValues={{
                        companyId: '',
                        name: '',
                        slug: '',
                        description: '',
                        configuration: '',
                        schemaVersion: '' as unknown as number,
                    }}
                    onSubmit={(values, { setErrors, setSubmitting }) => {
                        const jsonError = validateConfigurationJson(values.configuration);
                        if (jsonError) {
                            setErrors({ configuration: jsonError });
                            setSubmitting(false);
                            return;
                        }
                        postConfiguration({
                            CompanyId: values.companyId,
                            Name: values.name,
                            Slug: values.slug || undefined,
                            Description: values.description || undefined,
                            Configuration: values.configuration,
                            SchemaVersion: Number(values.schemaVersion),
                        })
                            .unwrap()
                            .then(() => {
                                setView('list');
                            })
                            .catch((err) => {
                                setSubmitting(false);
                                toast.error(
                                    err?.data?.ResponseStatus?.Message ??
                                    'Failed to create widget configuration',
                                    { autoClose: 5000 },
                                );
                            });
                    }}
                >
                    {(formProps) => {
                        const currentSchema = schemas.find(
                            (s) => s.Version === Number(formProps.values.schemaVersion),
                        )?.Schema;

                        return (
                            <Flex sx={{ flexDirection: 'column', gap: 3 }}>
                                <GrayFormikField
                                    name="companyId"
                                    label="Company ID"
                                    required
                                    autoComplete="off"
                                />
                                <GrayFormikField name="name" label="Name" required autoComplete="off" />
                                <GrayFormikField name="slug" label="Slug" autoComplete="off" />
                                <GrayFormikField type="textarea" name="description" label="Description" />
                                <ConfigurationJsonField
                                    name="configuration"
                                    rows={12}
                                    placeholder={'{\n  "someField": "value"\n}'}
                                    schema={currentSchema}
                                />
                                <GrayFormikField
                                    type="number"
                                    name="schemaVersion"
                                    label="Schema version"
                                    required
                                    placeholder="1"
                                />
                                <Flex sx={{ justifyContent: 'flex-end', gap: 2 }}>
                                    <Button
                                        variant="secondary"
                                        type="button"
                                        onClick={() => setView('list')}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        type="submit"
                                        isLoading={formProps.isSubmitting}
                                        disabled={formProps.isSubmitting}
                                        onClick={formProps.submitForm}
                                    >
                                        Save
                                    </Button>
                                </Flex>
                            </Flex>
                        );
                    }}
                </Formik>
            </Container>
        );
    }

    return (
        <Container>
            {backToCompanyId && (
                <Flex sx={{ justifyContent: 'flex-end', mb: 3 }}>
                    <Button
                        type="button"
                        variant="secondary"
                        onClick={() => navigate(`/clients/${backToCompanyId}`)}
                    >
                        Back to company
                    </Button>
                </Flex>
            )}
            <Flex sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Heading as="h2">Widget configurations</Heading>
                <Button onClick={() => setView('create')}>Create configuration</Button>
            </Flex>
            <Box sx={{ maxWidth: 480, mb: 3 }}>
                <SearchBy
                    onSearch={setCompanyId}
                    placeholder="Search by Company ID..."
                    initialValue={routeCompanyId ?? ''}
                />
            </Box>
            {trimmed.length === 0 ? (
                <Text>Enter a Company ID above to find widget configurations.</Text>
            ) : isLoading ? (
                <Spinner size={16} />
            ) : configurations.length === 0 ? (
                <Text>No widget configurations found.</Text>
            ) : (
                configurations.map((config) => (
                    <ConfigurationRow
                        key={config.Id}
                        config={config}
                        expanded={expandedIds.has(config.Id!)}
                        onToggle={() => toggleExpanded(config.Id!)}
                        companySitepath={sitePath}
                        embedScriptUrl={embedScriptUrl}
                        schemas={schemas}
                    />
                ))
            )}
        </Container>
    );
};

export default WidgetConfigurations;
